import mixpanelWeb from 'mixpanel-browser';

import { ApolloClient } from '@apollo/client';

import config from 'lane-shared/config';
import { debounce } from './debounce';
import { AnalyticsHelper } from 'lane-shared/helpers';
import { ANALYTIC_KEYS, AnalyticKey } from 'constants-analytics';

import history from './history';
import {
  AnalyticsDataType,
  AnalyticsInterface,
} from 'packages/lane-shared/types/Analytics';

let analytics: AnalyticsHelper;
let lastLocation: any;
let mixpanelInitialized = false;
let historyListenerSetup = false;
let mixpanelFeatureFlagEnabled: boolean | null = null;
let mixpanelConsentGranted = false;

/**
 * Bridge between the React tree (where the LD flag lives via useFlag)
 * and non-React code (Klaro consent callback). Sets the flag state so
 * that isMixpanelFeatureEnabled() returns the correct value when
 * initializeMixpanel() is called by Klaro consent or the !klaroEnabled fallback.
 *
 * Does NOT eagerly initialize on first load (to avoid bypassing Klaro consent),
 * but WILL re-initialize if consent was previously granted and Mixpanel was
 * shut down by a prior FF toggle.
 */
export function setMixpanelFeatureFlagEnabled(enabled: boolean, userId?: string | null): void {
  mixpanelFeatureFlagEnabled = enabled;

  if (enabled && !config.mixpanel.enabled) {
    if (mixpanelInitialized) {
      shutdownMixpanel(true);
    }

    return;
  }

  if (!mixpanelInitialized && mixpanelConsentGranted) {
    initializeMixpanel();
  }

  if (userId && mixpanelInitialized) {
    mixpanelWeb.identify(userId);
  }
}

function isMixpanelFeatureEnabled(): boolean {
  if (mixpanelFeatureFlagEnabled) {
    return Boolean(config.mixpanel.enabled);
  }

  return true;
}

/**
 * Initialize Mixpanel. Idempotent - safe to call multiple times.
 * Called by Klaro consent callback when user grants analytics consent.
 * Gated by the MixpanelEnabled feature flag (via setMixpanelFeatureFlagEnabled)
 * with config.mixpanel.enabled as the static fallback.
 */
export function initializeMixpanel(): void {
  if (mixpanelInitialized || typeof window === 'undefined') {
    return;
  }

  mixpanelConsentGranted = true;

  if (!isMixpanelFeatureEnabled()) {
    return;
  }

  mixpanelWeb.init(config.mixpanel.token);
  mixpanelWeb.opt_in_tracking();
  mixpanelInitialized = true;
}

/**
 * Shutdown Mixpanel tracking.
 * @param preserveConsent - When true, keeps mixpanelConsentGranted so
 *   the FF bridge can re-init later. Klaro consent revocation calls
 *   this without the flag, which clears consent permanently.
 */
export function shutdownMixpanel(preserveConsent = false): void {
  if (!preserveConsent) {
    mixpanelConsentGranted = false;
  }

  if (!mixpanelInitialized) {
    return;
  }

  try {
    mixpanelWeb.reset();
    mixpanelWeb.opt_out_tracking();
  } catch {
    // Ignore
  }

  mixpanelInitialized = false;
}

// Check if Mixpanel has been initialized (consent granted).
export function isMixpanelInitialized(): boolean {
  return mixpanelInitialized;
}

export function analyticsHelper(client: ApolloClient<any>): AnalyticsInterface {
  if (!analytics) {
    analytics = new AnalyticsHelper({ client });

    track(ANALYTIC_KEYS.ANALYTIC_PATH, {
      path: history.location.pathname,
      category: 'enter',
      value: (history.location as any).query,
    });

    lastLocation = history.location;
  }

  // Setup history listener once (independent of Mixpanel state)
  if (!historyListenerSetup) {
    historyListenerSetup = true;

    history.listen(
      // TODO: Fix the type "any"
      debounce((location: any) => {
        if (lastLocation) {
          track(ANALYTIC_KEYS.ANALYTIC_PATH, {
            path: lastLocation.pathname,
            category: 'leave',
            value: lastLocation.query,
          });
        }

        track(ANALYTIC_KEYS.ANALYTIC_PATH, {
          path: location.pathname,
          category: 'enter',
          value: (location as any).query,
        });

        lastLocation = location;
      }, 240)
    );
  }

  function track(event: AnalyticKey, data: AnalyticsDataType) {
    analytics.track(event, { ...data });

    // Only call Mixpanel if initialized (consent granted)
    if (mixpanelInitialized) {
      mixpanelWeb.track(event, { ...data });
    }
  }

  function trackMixpanel(event: AnalyticKey, data: AnalyticsDataType) {
    if (mixpanelInitialized) {
      mixpanelWeb.track(event, { ...data });
    }
  }

  function setMixpanelSessionId(sessionId: string) {
    if (mixpanelInitialized) {
      mixpanelWeb.register({ sessionId });
    }
  }

  function setChannel(channelId: string, channelName: string) {
    if (mixpanelInitialized) {
      mixpanelWeb.register({ channelId, channelName });
    }
  }

  function identify(userId: string) {
    if (mixpanelInitialized) {
      mixpanelWeb.identify(userId);
    }
  }

  function update(props: Record<string, unknown>) {
    if (mixpanelInitialized) {
      mixpanelWeb.people.set(props);
    }
  }

  return {
    track,
    trackMixpanel,
    identify,
    update,
    setMixpanelSessionId,
    setChannel,
    onNavigationStateChange: () => {},
  };
}
